import { useEffect, useRef, useState } from 'react';

import { Box } from '@vimeo/bokeh';
import { BackgroundVideo } from '@vimeo/gctl-api/models/components/schemas/background-video/BackgroundVideo';
import { usePlayer } from '@vimeo/player';

const TRIMMER_ROUND_OFFSET = 0.501;
const CARD_HEADER_SIZE = 80;

function MiniPlayer({
  backgroundVideo,
  elemWidth,
  isVisible,
}: {
  backgroundVideo: BackgroundVideo;
  elemWidth: number;
  isVisible: boolean;
}) {
  const {
    aspectRatio: initialAspectRatio,
    clipId,
    configUrl,
    endTime,
    firstFrameUrl,
    startTime,
    yPosition,
  } = backgroundVideo;
  const playerRef = useRef<HTMLDivElement>(null);
  const aspectRatio = useRef(initialAspectRatio);
  const [loading, setLoading] = useState(true);

  const { player } = usePlayer(playerRef, clipId, true, configUrl || '', true, 'auto');

  // Initialize player
  useEffect(() => {
    const onTimeUpdate = () => {
      if (player.currentTime >= endTime - TRIMMER_ROUND_OFFSET) {
        player.currentTime = startTime;
      }
    };

    if (player) {
      player.currentTime = startTime;
      setLoading(false);
      aspectRatio.current = player.videoWidth / player.videoHeight;
      player.on('timeupdate', onTimeUpdate);
    }

    return () => {
      if (player) {
        player.off('timeupdate', onTimeUpdate);
      }
    };
  }, [endTime, player, startTime]);

  // Play or pause video
  useEffect(() => {
    if (player) {
      if (isVisible) {
        player.play();
      } else {
        player.pause();
      }
    }
  }, [isVisible, player]);

  // For backgroundVideo, yPosition is a percentage of the video's height
  const yOffset = `${(-1 * CARD_HEADER_SIZE * (yPosition || 50)) / 100}px`;

  return (
    <Box
      w={elemWidth}
      h={CARD_HEADER_SIZE}
      position="absolute"
      overflow="hidden"
      borderTopRadius="drawer"
    >
      {loading ? (
        <Box
          display="block"
          sx={{
            video: {
              display: 'block',
              width: '100%',
            },
          }}
          position="absolute"
          left={0}
          right={0}
          top={yOffset}
        >
          <img src={firstFrameUrl} alt={clipId.toString()} width={elemWidth} height="auto" />
        </Box>
      ) : null}
      <Box
        sx={{
          display: loading ? 'none' : 'block',
          video: {
            display: 'block',
            width: '100%',
          },
        }}
        position="absolute"
        left={0}
        right={0}
        top={yOffset}
        ref={playerRef}
      />
    </Box>
  );
}

export default MiniPlayer;
